import React, { useState, useEffect, useRef, useCallback } from 'react';
import DatePicker from '../common/datePicker/LazyDatePicker';
import CalendarShimmer from '../common/datePicker/CalendarShimmer';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Card,
  CardContent,
  Autocomplete,
  useMediaQuery
} from '@mui/material';
import TrainIcon from '@mui/icons-material/Train';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import swap_icon from '../../assets/swap_icon.svg';
import checkboxFilled from '../../assets/checkboxes filled.svg';
import checkboxEmpty from '../../assets/checkboxes empty.svg';
import { useNavigate } from 'react-router-dom';
import { useSearchContext } from '../../context/SearchContext';
import { apiService } from '../../services/api';
import { trainStationCatalog } from '../train_select/trainStationCatalog';
import { useAuth } from '../../features/auth';
import { MOBILE_HOMEPAGE_LAYOUT as L } from './mobileHomepageLayout';
import TrainComingSoonDisclaimer from '../common/TrainComingSoonDisclaimer';

const TrainForm = ({ onStateChange, onAuthRequired }) => {
  /* Match FlightForm / FlightForm.css — viewport under 768px */
  const isMobile = useMediaQuery('(max-width: 1023.98px)');
  const [disclaimerOpen, setDisclaimerOpen] = useState(true);

  // State variables
  const [fromStation, setFromStation] = useState(null);
  const [toStation, setToStation] = useState(null);
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [fromOptions, setFromOptions] = useState([]);
  const [toOptions, setToOptions] = useState([]);
  const [fromLoading, setFromLoading] = useState(false);
  const [toLoading, setToLoading] = useState(false);
  const [fromDropdownOpen, setFromDropdownOpen] = useState(false);
  const [toDropdownOpen, setToDropdownOpen] = useState(false);
  const [journeyDate, setJourneyDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerPosition, setDatePickerPosition] = useState({ top: 0, left: 0 });
  const [coaches, setCoaches] = useState('');
  const [quota, setQuota] = useState('GN'); // GN = General
  const [quotaDropdownOpen, setQuotaDropdownOpen] = useState(false);
  const [showNearbyStations, setShowNearbyStations] = useState(false);
  const [flexibleDates, setFlexibleDates] = useState(false);

  const navigate = useNavigate();
  const { updateSearchParams } = useSearchContext();
  const { isAuthenticated } = useAuth();
  
  // Refs for input fields
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  useEffect(() => {
    trainStationCatalog.loadStations();
  }, []);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('[data-date-picker]')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Mock train stations data - replace with actual API
  const mockStations = [
    { code: 'NDLS', name: 'New Delhi', city: 'Delhi' },
    { code: 'CSMT', name: 'Chhatrapati Shivaji Maharaj Terminus', city: 'Mumbai' },
    { code: 'HWH', name: 'Howrah Junction', city: 'Kolkata' },
    { code: 'SBC', name: 'Bangalore City Junction', city: 'Bangalore' },
    { code: 'MAS', name: 'Chennai Central', city: 'Chennai' },
    { code: 'PNBE', name: 'Patna Junction', city: 'Patna' },
    { code: 'LKO', name: 'Lucknow Charbagh', city: 'Lucknow' },
    { code: 'JP', name: 'Jaipur Junction', city: 'Jaipur' },
    { code: 'ADI', name: 'Ahmedabad Junction', city: 'Ahmedabad' },
    { code: 'HYB', name: 'Hyderabad Deccan', city: 'Hyderabad' }
  ];

  // Mock coaches data
  const coachesOptions = [
    'AC First Class',
    'AC 2 Tier',
    'AC 3 Tier',
    'Sleeper',
    'General'
  ];

  // IRCTC Quota options
  const quotaOptions = [
    { value: 'GN', label: 'General' },
    { value: 'TQ', label: 'Tatkal' },
    { value: 'PT', label: 'Premium Tatkal' },
    { value: 'LD', label: 'Ladies' },
    { value: 'HP', label: 'Physically Handicapped' },
    { value: 'SS', label: 'Senior Citizen' },
    { value: 'YU', label: 'Yuva' },
    { value: 'DP', label: 'Defence' },
    { value: 'FT', label: 'Foreign Tourist' },
    { value: 'OS', label: 'Out Station' }
  ];

  // Search in JSON file first with priority ranking
  const searchInJSON = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const stations = trainStationCatalog.getLoadedStations();
    
    // Search by code, name, or city (case-insensitive) with priority scoring
    const matches = stations.map(station => {
      const code = station.stnCode?.toLowerCase() || '';
      const name = station.stnName?.toLowerCase() || '';
      const city = station.stnCity?.toLowerCase() || '';
      
      let priority = 0; // Higher priority = better match
      let matched = false;
      
      // Check exact match (highest priority = 3)
      if (code === searchLower || name === searchLower || city === searchLower) {
        priority = 3;
        matched = true;
      }
      // Check starts with (second priority = 2)
      else if (code.startsWith(searchLower) || name.startsWith(searchLower) || city.startsWith(searchLower)) {
        priority = 2;
        matched = true;
      }
      // Check contains (lowest priority = 1)
      else if (code.includes(searchLower) || name.includes(searchLower) || city.includes(searchLower)) {
        priority = 1;
        matched = true;
      }
      
      if (matched) {
        return {
          code: station.stnCode,
          name: station.stnName,
          city: station.stnCity,
          priority: priority
        };
      }
      return null;
    }).filter(Boolean);

    // Sort by priority (highest first), then alphabetically by name
    matches.sort((a, b) => {
      if (b.priority !== a.priority) {
        return b.priority - a.priority; // Higher priority first
      }
      // If same priority, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });

    // Remove priority from final result
    return matches.map(({ priority, ...station }) => station);
  }, []);

  // Handle station search - JSON first, then API fallback
  const handleStationSearch = useCallback(async (input, setOptions, setLoading) => {
    if (input.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    
    try {
      // First, search in JSON file
      await trainStationCatalog.loadStations();
      const jsonResults = searchInJSON(input);
      
      if (jsonResults.length > 0) {
        // If we have results from JSON, use them
        setOptions(jsonResults);
        setLoading(false);
        return;
      }

      // If no JSON results, fall back to API
      const response = await apiService.searchStations(input);
      if (response.data.success && response.data.stations) {
        // Ensure API results have the correct format
        const apiResults = response.data.stations.map(station => ({
          code: station.code || station.stnCode,
          name: station.name || station.stnName,
          city: station.city || station.stnCity || station.name || station.stnName
        }));
        setOptions(apiResults);
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.error('Station search error:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [searchInJSON]);

  // Handle from station input change
  const handleFromInputChange = (event) => {
    const value = event.target.value;
    setFromInput(value);
    // Clear station state when input is cleared
    if (!value || value.trim() === '') {
      setFromStation(null);
    }
    handleStationSearch(value, setFromOptions, setFromLoading);
  };

  // Handle to station input change
  const handleToInputChange = (event) => {
    const value = event.target.value;
    setToInput(value);
    // Clear station state when input is cleared
    if (!value || value.trim() === '') {
      setToStation(null);
    }
    handleStationSearch(value, setToOptions, setToLoading);
  };

  // Handle station selection
  const handleFromStationSelect = (station) => {
    // Prevent selecting same station as "To"
    if (toStation && station.code === toStation.code) {
      // Show warning and prevent selection
      alert(`Please select a different station. "${station.name} (${station.code})" is already selected as destination.`);
      return;
    }
    
    setFromStation(station);
    setFromInput(station ? `${station.name} (${station.code})` : '');
    setFromDropdownOpen(false);
    
    // Auto-open and focus on "to" dropdown
    if (!toStation?.code) {
      setTimeout(() => {
        setToDropdownOpen(true);
        // Load options if needed - search for popular stations
        if (!toInput || toInput.length < 2) {
          handleStationSearch('Delhi', setToOptions, setToLoading);
        }
        // Focus the to input field
        setTimeout(() => {
          if (toInputRef.current) {
            toInputRef.current.focus();
          }
        }, 50);
      }, 100);
    }
  };

  const handleToStationSelect = (station) => {
    // Prevent selecting same station as "From"
    if (fromStation && station.code === fromStation.code) {
      // Show warning and prevent selection
      alert(`Please select a different station. "${station.name} (${station.code})" is already selected as origin.`);
      return;
    }
    
    setToStation(station);
    setToInput(station ? `${station.name} (${station.code})` : '');
    setToDropdownOpen(false);
    
    // Auto-open date picker
    if (!journeyDate) {
      setTimeout(() => {
        setShowDatePicker(true);
      }, 100);
    }
  };

  // Swap stations
  const handleSwapStations = () => {
    const tempStation = fromStation;
    const tempInput = fromInput;
    const tempOptions = fromOptions;

    setFromStation(toStation);
    setFromInput(toInput);
    setFromOptions(toOptions);

    setToStation(tempStation);
    setToInput(tempInput);
    setToOptions(tempOptions);
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle search
  const handleSearch = () => {
    // Validation and auto-open dropdowns/pickers (similar to FlightForm one-way)
    if (!fromStation) {
      setFromDropdownOpen(true);
      // Load popular stations from JSON if input is empty
      if (!fromInput || fromInput.length < 2) {
        // Search for popular stations (e.g., major cities) - use handleStationSearch which checks JSON first
        handleStationSearch('Delhi', setFromOptions, setFromLoading);
      }
      // Focus the from input field
      setTimeout(() => {
        if (fromInputRef.current) {
          const inputElement = fromInputRef.current.querySelector('input');
          if (inputElement) {
            inputElement.focus();
          }
        }
      }, 100);
      return;
    }
    
    if (!toStation) {
      setToDropdownOpen(true);
      // Load popular stations from JSON if input is empty
      if (!toInput || toInput.length < 2) {
        // Search for popular stations (e.g., major cities) - use handleStationSearch which checks JSON first
        handleStationSearch('Mumbai', setToOptions, setToLoading);
      }
      // Focus the to input field
      setTimeout(() => {
        if (toInputRef.current) {
          const inputElement = toInputRef.current.querySelector('input');
          if (inputElement) {
            inputElement.focus();
          }
        }
      }, 100);
      return;
    }

    if (!journeyDate) {
      setShowDatePicker(true);
      return;
    }

    const formattedDate = formatDate(journeyDate);
    
    // Update global search context
    updateSearchParams({
      from: fromStation.code,
      to: toStation.code,
      fromName: fromStation.name,
      toName: toStation.name,
      journeyDate: formattedDate,
      coaches: coaches,
      quota: quota,
      showNearbyStations: showNearbyStations,
      flexibleDates: flexibleDates,
      searchType: 'train'
    });

    // Update URL with form values (including station names and cities)
    const params = new URLSearchParams({
      from: fromStation.code,
      to: toStation.code,
      journeyDate: formattedDate,
      quota: quota || 'GN'
    });

    // Add station names if available
    if (fromStation.name) {
      params.append('fromName', fromStation.name);
    }
    if (fromStation.city) {
      params.append('fromCity', fromStation.city);
    }
    if (toStation.name) {
      params.append('toName', toStation.name);
    }
    if (toStation.city) {
      params.append('toCity', toStation.city);
    }

    // Add optional parameters
    if (coaches) {
      params.append('coaches', coaches);
    }
    if (showNearbyStations) {
      params.append('nearbyStations', showNearbyStations.toString());
    }
    if (flexibleDates) {
      params.append('flexibleDates', flexibleDates.toString());
    }

    navigate(`/train?${params.toString()}`);
  };

  // Handle Tatkal buttons
  const handleTatkalTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setJourneyDate(tomorrow);
  };

  const handleTatkalDayAfterTomorrow = () => {
    const dayAfterTomorrow = new Date();
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    setJourneyDate(dayAfterTomorrow);
  };

  return (
    <>
    <TrainComingSoonDisclaimer
      open={disclaimerOpen}
      onClose={() => setDisclaimerOpen(false)}
    />
    <Box sx={{
      position: 'relative',
      zIndex: { xs: 'auto', sm: 'auto', md: 'auto' },
      mt: { xs: '0px', sm: '0px', md: '-80px', lg: '-90px', xl: '-90px' },
      width: {
        xs: '100%',
        sm: '100%',
        md: 'calc(100% - 80px)',
        lg: 'calc(100% - 96px)',
        xl: 'calc(100% - 96px)',
      },
      maxWidth: {
        xs: '100%',
        sm: '100%',
        md: '1160px',
        lg: '1360px',
        xl: '1560px',
      },
      mx: 'auto',
      px: { xs: 0, sm: 0, md: 0 },
    }}>
      <Card sx={{
        borderRadius: { xs: L.formCardRadius, sm: L.formCardRadius, md: '32px' },
        border: '1px solid var(--yovo-grey-300, #D6D9E0)',
        background: { xs: '#E8E9ED', sm: '#E8E9ED', md: 'var(--Neutrals-Base-white, #FBFBFB)' },
        boxShadow: 'var(--yovo-shadow-card, 0 0 23px 0 rgba(157, 157, 157, 0.25))',
        overflow: 'visible',
        width: { xs: '100%', sm: '100%', md: 'auto' },
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: { xs: 2, sm: 2, md: 'auto' },
      }}>
        <CardContent sx={{
          pt: { xs: L.formCardPaddingY, sm: L.formCardPaddingY, md: '24px' },
          pb: { xs: L.formCardPaddingY, sm: L.formCardPaddingY, md: '24px' },
          pl: { xs: L.formCardPaddingX, sm: L.formCardPaddingX, md: '40px' },
          pr: { xs: L.formCardPaddingX, sm: L.formCardPaddingX, md: '40px' },
          position: 'relative',
        }}>
          {/* Top spacing to match FlightForm */}
          <Box sx={{ 
              display: 'flex', 
              gap: '0px', 
              mb: 0.5, 
              alignItems: 'center', 
              flexWrap: 'wrap',
              pb: 0.3
            }}>
              
            
              <Typography sx={{ 
                fontSize: '0.75rem', 
                color: '#666', 
                fontWeight: 500,
                ml: 'auto',
                alignSelf: 'center',
                marginBottom: '9.75px'
              }}>
                
              </Typography>
            </Box>

          {/* IRCTC Notice */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 1.5,
            mt: -1.5,
          }}>
            <Typography sx={{
              color: 'rgba(126, 128, 143, 1)',
              fontFamily: 'Host Grotesk',
              fontSize: '14px',
              fontWeight: 400,
              textAlign: 'center',
              opacity: 0.7,
              lineHeight: 1.2
            }}>
              Train Search supports IRCTC routes within India Only
            </Typography>
          </Box>

          {/* Search Form Fields — Figma inner Filter box on mobile (326×312 @ 350px card) */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'column', md: 'row' },
            gap: { xs: 0, sm: 0, md: 1.5 }, 
            alignItems: { xs: 'stretch', sm: 'stretch' }, 
            mb: 1, 
            width: '100%',
            flexWrap: { xs: 'nowrap', sm: 'nowrap', md: 'wrap' },
            background: { xs: 'var(--yovo-base-white, #FBFBFB)', sm: 'var(--yovo-base-white, #FBFBFB)', md: 'var(--yovo-base-white, #fff)' },
            border: { xs: '1px solid var(--neutrals-grey-400, #DAD9E0)', sm: '1px solid var(--neutrals-grey-400, #DAD9E0)', md: '1px solid #d0d0d0' },
            borderRadius: { xs: L.innerBoxRadius, sm: L.innerBoxRadius, md: 5 },
            padding: { xs: L.innerBoxPadding, sm: L.innerBoxPadding, md: '16px 20px' },
            pl: { xs: L.innerBoxPadding, sm: L.innerBoxPadding, md: '16px' },
            pr: { xs: L.innerBoxPadding, sm: L.innerBoxPadding, md: '16px' },
            boxSizing: 'border-box',
          }}>
            
            {/* FROM Field */}
            <Box sx={{ 
              flex: { xs: '1 1 auto', sm: '1 1 auto', md: '1 1 0' }, 
              minWidth: { xs: 0, sm: 0, md: '100px' }, 
              width: { xs: '100%', sm: '100%', md: 'auto' }, 
              display: 'flex', 
              flexDirection: 'column',
              py: { xs: '10px', sm: '10px', md: 0 },
              pl: { xs: '4px', sm: '4px', md: 0 },
              pr: { xs: '4px', sm: '4px', md: 0 },
              borderBottom: { xs: '1px solid #E8E9ED', sm: '1px solid #E8E9ED', md: 'none' },
            }}>
              <Typography sx={{ 
                color: 'var(--Neutrals-base-black, #20202A)',
                fontFamily: 'Host Grotesk',
                fontSize: { xs: '12px', sm: '12px', md: '14px' },
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '20px',
                letterSpacing: '0.15px',
                mb: 0.3,
                textAlign: 'left',
              }}>
                From
              </Typography>
              <Box sx={{ position: 'relative', width: '100%', marginLeft: 0 }} ref={fromInputRef}>
                <TextField
                  fullWidth
                  placeholder="Departure Stati..."
                  value={fromInput}
                  onChange={handleFromInputChange}
                  onFocus={() => setFromDropdownOpen(true)}
                  size="small"
                  autoComplete="off"
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      minHeight: '36px',
                      height: '36px',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      '& fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#e0e0e0',
                        boxShadow: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      overflow: 'hidden',
                      color: 'var(--Neutrals-grey-700, #7E808F)',
                      '&:not(:placeholder-shown)': {
                        color: 'var(--Neutrals-base-black, #20202A)',
                      },
                      textOverflow: 'ellipsis',
                      fontFamily: 'Host Grotesk',
                      fontSize: { xs: 'max(16px, 1rem)', sm: 'max(16px, 1rem)', md: '20px' },
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: { xs: '22px', sm: '22px', md: '28px' },
                      width: '100%',
                      whiteSpace: 'nowrap',
                      padding: '0 !important',
                      margin: 0,
                    }
                  }}
                />
                {fromDropdownOpen && fromOptions.length > 0 && (
                  <Box sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    maxHeight: { xs: 'min(55vh, 400px)', sm: 'min(55vh, 400px)', md: '400px' },
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    minWidth: { xs: 0, sm: 0, md: '350px' },
                    padding: '4px 0',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#000000',
                      borderRadius: '3px',
                      border: 'none',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#333333',
                    },
                  }}>
                    {fromOptions
                      .filter(station => !toStation || station.code !== toStation.code) // Filter out selected "To" station
                      .map((station, index) => (
                      <Box
                        key={index}
                        onClick={() => handleFromStationSelect(station)}
                        sx={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'flex-start',
                          minHeight: '60px',
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        <TrainIcon sx={{
                          width: '18px',
                          height: '18px',
                          marginRight: '12px',
                          flexShrink: 0,
                          marginTop: '2px',
                          color: '#000000'
                        }} />
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            width: '100%'
                          }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{
                                fontWeight: 600,
                                color: '#20202A',
                                fontSize: '14px',
                                marginBottom: '4px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {station.city || station.name}
                              </Typography>
                              <Typography sx={{
                                fontSize: '12px',
                                color: '#7E808F',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {station.name}
                              </Typography>
                            </Box>
                            <Typography sx={{
                              fontWeight: 500,
                              color: '#20202A',
                              fontSize: '14px',
                              flexShrink: 0,
                              marginLeft: '12px',
                              alignSelf: 'flex-start'
                            }}>
                              {station.code}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Swap Button */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              flexShrink: 0,
              width: { xs: '100%', sm: '100%', md: 'auto' },
              py: { xs: '6px', sm: '6px', md: 0 },
              order: { xs: 0, sm: 0 },
            }}>
              <IconButton
                onClick={handleSwapStations}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #AEAEB8',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  '&:hover': {
                    backgroundColor: '#F5F5F5'
                  }
                }}
              >
                <img 
                  src={swap_icon} 
                  alt="swap" 
                  style={{
                    width: '16px',
                    height: '16px'
                  }}
                />
              </IconButton>
            </Box>

            {/* TO Field */}
            <Box sx={{ 
              flex: { xs: '1 1 auto', sm: '1 1 auto', md: '1 1 0' }, 
              minWidth: { xs: 0, sm: 0, md: '100px' }, 
              width: { xs: '100%', sm: '100%', md: 'auto' }, 
              display: 'flex', 
              flexDirection: 'column',
              py: { xs: '10px', sm: '10px', md: 0 },
              pl: { xs: '4px', sm: '4px', md: 0 },
              pr: { xs: '4px', sm: '4px', md: 0 },
              borderBottom: { xs: '1px solid #E8E9ED', sm: '1px solid #E8E9ED', md: 'none' },
            }}>
              <Typography sx={{ 
                color: 'var(--Neutrals-base-black, #20202A)',
                fontFamily: 'Host Grotesk',
                fontSize: { xs: '12px', sm: '12px', md: '14px' },
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '20px',
                letterSpacing: '0.15px',
                mb: 0.3,
                textAlign: 'left',
              }}>
                To
              </Typography>
              <Box sx={{ position: 'relative', width: '100%', marginLeft: 0 }} ref={toInputRef}>
                <TextField
                  fullWidth
                  placeholder="Arrival Station"
                  value={toInput}
                  onChange={handleToInputChange}
                  onFocus={() => setToDropdownOpen(true)}
                  size="small"
                  autoComplete="off"
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      minHeight: '36px',
                      height: '36px',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      '& fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#e0e0e0',
                        boxShadow: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      overflow: 'hidden',
                      color: 'var(--Neutrals-grey-700, #7E808F)',
                      '&:not(:placeholder-shown)': {
                        color: 'var(--Neutrals-base-black, #20202A)',
                      },
                      textOverflow: 'ellipsis',
                      fontFamily: 'Host Grotesk',
                      fontSize: { xs: 'max(16px, 1rem)', sm: 'max(16px, 1rem)', md: '20px' },
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: { xs: '22px', sm: '22px', md: '28px' },
                      width: '100%',
                      whiteSpace: 'nowrap',
                      padding: '0 !important',
                      margin: 0,
                    }
                  }}
                />
                {toDropdownOpen && toOptions.length > 0 && (
                  <Box sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    maxHeight: { xs: 'min(55vh, 400px)', sm: 'min(55vh, 400px)', md: '400px' },
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    minWidth: { xs: 0, sm: 0, md: '350px' },
                    padding: '4px 0',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#000000',
                      borderRadius: '3px',
                      border: 'none',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#333333',
                    },
                  }}>
                    {toOptions
                      .filter(station => !fromStation || station.code !== fromStation.code) // Filter out selected "From" station
                      .map((station, index) => (
                      <Box
                        key={index}
                        onClick={() => handleToStationSelect(station)}
                        sx={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'flex-start',
                          minHeight: '60px',
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        <TrainIcon sx={{
                          width: '18px',
                          height: '18px',
                          marginRight: '12px',
                          flexShrink: 0,
                          marginTop: '2px',
                          color: '#000000'
                        }} />
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            width: '100%'
                          }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{
                                fontWeight: 600,
                                color: '#20202A',
                                fontSize: '14px',
                                marginBottom: '4px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {station.city || station.name}
                              </Typography>
                              <Typography sx={{
                                fontSize: '12px',
                                color: '#7E808F',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {station.name}
                              </Typography>
                            </Box>
                            <Typography sx={{
                              fontWeight: 500,
                              color: '#20202A',
                              fontSize: '14px',
                              flexShrink: 0,
                              marginLeft: '12px',
                              alignSelf: 'flex-start'
                            }}>
                              {station.code}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Coaches Field */}
            {/* <Box sx={{ flex: 1, minWidth: '100px', width: '8.25rem', display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ 
                color: 'var(--Neutrals-base-black, #20202A)',
                fontFamily: 'Host Grotesk',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '20px',
                letterSpacing: '0.15px',
                mb: 0.3,
                textAlign: 'left',
              }}>
                Coaches
              </Typography>
              <FormControl fullWidth sx={{ marginLeft: 0 }}>
                <Select
                  value={coaches}
                  onChange={(e) => setCoaches(e.target.value)}
                  displayEmpty
                  size="small"
                  sx={{
                    borderRadius: 1,
                    minHeight: '36px',
                    height: '36px',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiSelect-select': {
                      color: 'var(--Neutrals-grey-700, #7E808F)',
                      fontFamily: 'Host Grotesk',
                      fontSize: '20px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '28px',
                      padding: '0 !important',
                      margin: 0,
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {coachesOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box> */}

            {/* Quota Field */}
            <Box sx={{
              flex: { xs: '1 1 auto', sm: '1 1 auto', md: 1 },
              minWidth: { xs: 0, sm: 0, md: '100px' },
              width: { xs: '100%', sm: '100%', md: '8.25rem' },
              display: 'flex',
              flexDirection: 'column',
              py: { xs: '10px', sm: '10px', md: 0 },
              pl: { xs: '4px', sm: '4px', md: 0 },
              pr: { xs: '4px', sm: '4px', md: 0 },
              borderBottom: { xs: '1px solid #E8E9ED', sm: '1px solid #E8E9ED', md: 'none' },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                <Typography sx={{ 
                  color: 'var(--Neutrals-base-black, #20202A)',
                  fontFamily: 'Host Grotesk',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: '0.15px',
                  textAlign: 'left',
                  mr: 0.5
                }}>
                  Quota
                </Typography>
                <KeyboardArrowDownIcon sx={{ 
                  fontSize: '18px',
                  color: 'var(--Neutrals-base-black, #20202A)',
                  transition: 'transform 0.3s ease',
                  transform: quotaDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }} />
              </Box>
              <FormControl fullWidth sx={{ marginLeft: 0 }}>
                <Select
                  value={quota}
                  onChange={(e) => setQuota(e.target.value)}
                  onOpen={() => setQuotaDropdownOpen(true)}
                  onClose={() => setQuotaDropdownOpen(false)}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    minHeight: '36px',
                    height: '36px',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiSelect-select': {
                      color: 'var(--Neutrals-grey-700,rgb(3, 3, 3))',
                      fontFamily: 'Host Grotesk',
                      fontSize: { xs: 'max(16px, 1rem)', sm: 'max(16px, 1rem)', md: '20px' },
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: { xs: '22px', sm: '22px', md: '28px' },
                      padding: '0 !important',
                      margin: 0,
                    },
                    '& .MuiSelect-icon': {
                      display: 'none'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 280, borderRadius: '12px' },
                    },
                  }}
                >
                  {quotaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Date Field */}
            <Box sx={{
              flex: { xs: '1 1 auto', sm: '1 1 auto', md: 1.2 },
              minWidth: { xs: 0, sm: 0, md: '100px' },
              width: { xs: '100%', sm: '100%', md: '8.25rem' },
              display: 'flex',
              flexDirection: 'column',
              py: { xs: '10px', sm: '10px', md: 0 },
              pl: { xs: '4px', sm: '4px', md: 0 },
              pr: { xs: '4px', sm: '4px', md: 0 },
              borderBottom: { xs: '1px solid #E8E9ED', sm: '1px solid #E8E9ED', md: 'none' },
            }}>
              <Typography sx={{ 
                color: 'var(--Neutrals-base-black, #20202A)',
                fontFamily: 'Host Grotesk',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '20px',
                letterSpacing: '0.15px',
                mb: 0.3,
                textAlign: 'left',
              }}>
                Date of Journey
              </Typography>
              <Box sx={{ position: 'relative', width: '100%', margin: 0, padding: 0 }} data-date-picker>
                <TextField
                  placeholder="Add Date"
                  size="small"
                  fullWidth
                  autoComplete="off"
                  readOnly
                  inputProps={{
                    readOnly: true,
                    style: { cursor: 'pointer', caretColor: 'transparent' }
                  }}
                  value={journeyDate ? journeyDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Add Date'}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowDatePicker(!showDatePicker);
                  }}
                  onFocus={(e) => {
                    e.target.blur();
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                        boxShadow: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: journeyDate ? 'var(--Neutrals-base-black, #20202A)' : 'var(--Neutrals-grey-700, #7E808F)',
                      fontFamily: '"Host Grotesk"',
                      fontSize: { xs: 'max(16px, 1rem)', sm: 'max(16px, 1rem)', md: '1.25rem' },
                      fontStyle: 'normal',
                      fontWeight: journeyDate ? 700 : 500,
                      lineHeight: { xs: '22px', sm: '22px', md: '1.75rem' },
                      cursor: 'pointer',
                      padding: '0 !important',
                      margin: 0,
                      paddingLeft: 0,
                      caretColor: 'transparent !important'
                    }
                  }}
                />
                
                {showDatePicker && (
                  <Box sx={{
                    position: 'absolute',
                    top: '100%',
                    left: { xs: 0, sm: 0, md: '50%' },
                    transform: { xs: 'none', sm: 'none', md: 'translateX(-50%)' },
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    padding: { xs: '10px', sm: '10px', md: '16px' },
                    minWidth: { xs: '100%', sm: '100%', md: '600px' },
                    width: { xs: '100%', sm: '100%', md: 'auto' },
                    maxWidth: { xs: '100%', sm: '100%', md: '650px' },
                    mt: 0.5
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      width: '100%',
                      mt: 0,
                      '& .react-datepicker': {
                        display: 'flex !important',
                        flexDirection: 'row !important',
                        gap: '12px !important',
                        '@media (max-width: 1023px)': {
                          flexDirection: 'column !important',
                        },
                      }
                    }}>
                      <DatePicker
                        suspenseFallback={<CalendarShimmer months={isMobile ? 1 : 2} />}
                        selected={journeyDate}
                        onChange={(date) => {
                          setJourneyDate(date);
                          if (date) {
                            setShowDatePicker(false);
                          }
                        }}
                        dateFormat="MMM dd, yyyy"
                        minDate={new Date()}
                        inline
                        showPopperArrow={false}
                        calendarClassName="custom-datepicker-single-month"
                        monthsShown={isMobile ? 1 : 2}
                        showPreviousMonths={false}
                        openToDate={journeyDate || new Date()}
                        selectsRange={false}
                        shouldCloseOnSelect={true}
                        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3)}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Tatkal Buttons */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 0.5,
              marginLeft: { xs: 0, sm: 0, md: '10px' },
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'center', md: 'flex-start' },
              width: { xs: '100%', sm: '100%', md: 'auto' },
              flexWrap: 'wrap',
              py: { xs: '8px', sm: '8px', md: 0 },
            }}>
              <Button
                onClick={handleTatkalTomorrow}
                sx={{
                  backgroundColor: 'transparent',
                  color: 'inherit',
                  borderRadius: '8px',
                  padding: 0,
                  minWidth: '60px',
                  height: '36px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                }}
              >
                <Box sx={{
                  backgroundColor: 'var(--Semantic-green-400, #167620);',
                  color: 'var(--Neutrals-Base-white, #FBFBFB)',
                  width: '100%',
                  padding: '4px 8px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textAlign: 'center',
                  borderRadius: '8px 8px 0 0',
                  fontFamily: 'Host Grotesk'
                }}>
                  Tatkal
                </Box>
                <Box sx={{
                  backgroundColor: ' var(--Neutrals-grey-200, #E8E9ED)',
                  color: '#666',
                  width: '100%',
                  padding: '4px 8px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textAlign: 'center',
                  borderRadius: '0 0 8px 8px',
                  fontFamily: 'Host Grotesk'
                }}>
                  Tomorrow
                </Box>
              </Button>
              <Button
                onClick={handleTatkalDayAfterTomorrow}
                sx={{
                  backgroundColor: 'transparent',
                  color: 'inherit',
                  borderRadius: '8px',
                  padding: 0,
                  minWidth: '60px',
                  height: '36px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                }}
              >
                <Box sx={{
    backgroundColor: 'var(--Semantic-green-400, #167620);',
    color: 'var(--Neutrals-Base-white, #FBFBFB)',
                  width: '100%',
                  padding: '4px 8px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textAlign: 'center',
                  borderRadius: '8px 8px 0 0',
                  fontFamily: 'Host Grotesk'
                }}>
                  Tatkal
                </Box>
                <Box sx={{
                     backgroundColor: ' var(--Neutrals-grey-200, #E8E9ED)',
                   color: 'var(--Neutrals-grey-800, #545665)',
                  width: '100%',
                  padding: '4px 8px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textAlign: 'center',
                  borderRadius: '0 0 8px 8px',
                  fontFamily: 'Host Grotesk'
                }}>
                  {(() => {
                    const dayAfterTomorrow = new Date();
                    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
                    const day = dayAfterTomorrow.getDate();
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const month = monthNames[dayAfterTomorrow.getMonth()];
                    const getOrdinalSuffix = (n) => {
                      const s = ['th', 'st', 'nd', 'rd'];
                      const v = n % 100;
                      return s[(v - 20) % 10] || s[v] || s[0];
                    };
                    return `${day}${getOrdinalSuffix(day)} ${month}`;
                  })()}
                </Box>
              </Button>
            </Box>

            {/* Let's Go Button */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: { xs: 0, sm: 0, md: '10px' },
              width: { xs: '100%', sm: '100%', md: 'auto' },
              py: { xs: '4px', sm: '4px', md: 0 },
            }}>
              <Button
                onClick={handleSearch}
                sx={{
                  background: '#000',
                  color: '#fff',
                  borderRadius: 4,
                  display: 'flex',
                  width: { xs: '100%', sm: '100%', md: 'auto' },
                  height: '3rem',
                  padding: '6px 20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '0.625rem',
                  letterSpacing: '0.4px',
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#333'
                  }
                }}
              >
                Let's Go
              </Button>
            </Box>
          </Box>

          {/* Checkboxes */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'column', md: 'row' },
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 1, md: 2 }, 
            mt: 1,
            alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
            justifyContent: 'center',
            px: { xs: '2px', sm: '2px', md: 0 },
          }}>
            <FormControlLabel
              control={
                <IconButton
                  onClick={() => setShowNearbyStations(!showNearbyStations)}
                  sx={{
                    p: 0.2,
                    width: '18px',
                    height: '18px',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={showNearbyStations ? checkboxFilled : checkboxEmpty}
                    alt="checkbox"
                    sx={{
                      width: '18px',
                      height: '18px',
                      display: 'block'
                    }}
                  />
                </IconButton>
              }
              label={
                <Typography sx={{ 
                  fontSize: '0.75rem',
                  color: '#666',
                  fontFamily: 'Host Grotesk'
                }}>
                  Show nearby stations
                </Typography>
              }
              sx={{ ml: 0, gap: '10px' }}
            />
            <FormControlLabel
              control={
                <IconButton
                  onClick={() => setFlexibleDates(!flexibleDates)}
                  sx={{
                    p: 0.2,
                    width: '18px',
                    height: '18px',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={flexibleDates ? checkboxFilled : checkboxEmpty}
                    alt="checkbox"
                    sx={{
                      width: '18px',
                      height: '18px',
                      display: 'block'
                    }}
                  />
                </IconButton>
              }
              label={
                <Typography sx={{ 
                  fontSize: '0.75rem',
                  color: '#666',
                  fontFamily: 'Host Grotesk'
                }}>
                  Flexible dates
                </Typography>
              }
              sx={{ ml: 0, gap: '10px' }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
    </>
  );
};

export default TrainForm;
